import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import AuthProvider from './components/AuthProvider';
import ProtectedRoute from './components/ProtectedRoute';
import { AppProvider, useAppContext } from './contexts/AppContext';
import { RBACProvider, useRBAC } from './contexts/RBACContext';
import JobListPage from './pages/JobListPage';
import JobDetailPage from './pages/JobDetailPage';
import CallbackPage from './pages/CallbackPage';
import LogoutPage from './pages/LogoutPage';
import ConfigurationPage from './pages/ConfigurationPage';
import MonitoringPage from './pages/MonitoringPage';
import SystemPage from './pages/SystemPage';

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAppContext();
  const { permissions, permissionsFailed } = useRBAC();

  return (
    <div className="min-h-screen bg-gray-100">
      {permissionsFailed && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-center text-sm text-yellow-800">
          ⚠️ Unable to load role permissions — running with full access. Contact your administrator if this persists.
        </div>
      )}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-2xl font-bold text-gray-900 hover:text-gray-700">
              <img src="/logo.png" alt="PrintFlow" className="h-8 w-8" />
              PrintFlow Dashboard
            </Link>
            <div className="flex items-center space-x-4">
              {isAuthenticated && user && (
                <>
                  {permissions?.logs && (
                    <Link
                      to="/"
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Logs
                    </Link>
                  )}
                  {permissions?.monitoring && (
                    <Link
                      to="/monitoring"
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Monitoring
                    </Link>
                  )}
                  {permissions?.configuration && (
                    <Link
                      to="/configuration"
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Configuration
                    </Link>
                  )}
                  {permissions?.system && (
                    <Link
                      to="/system"
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      System
                    </Link>
                  )}
                  <span className="text-sm text-gray-600">{user.email}</span>
                  <Link
                    to="/logout"
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Logout
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      <main>{children}</main>
      <footer className="bg-white border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-gray-500 text-center">
            Powered by LPi · Building Community Together
          </p>
        </div>
      </footer>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <AppLayout>
            <ProtectedRoute requiredTab="logs">
              <JobListPage />
            </ProtectedRoute>
          </AppLayout>
        }
      />
      <Route
        path="/jobs/:jobId"
        element={
          <AppLayout>
            <ProtectedRoute requiredTab="logs">
              <JobDetailPage />
            </ProtectedRoute>
          </AppLayout>
        }
      />
      <Route
        path="/configuration"
        element={
          <AppLayout>
            <ProtectedRoute requiredTab="configuration">
              <ConfigurationPage />
            </ProtectedRoute>
          </AppLayout>
        }
      />
      <Route
        path="/monitoring"
        element={
          <AppLayout>
            <ProtectedRoute requiredTab="monitoring">
              <MonitoringPage />
            </ProtectedRoute>
          </AppLayout>
        }
      />
      <Route
        path="/system"
        element={
          <AppLayout>
            <ProtectedRoute requiredTab="system">
              <SystemPage />
            </ProtectedRoute>
          </AppLayout>
        }
      />
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/logout" element={<LogoutPage />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <Router>
          <AuthProvider>
            <RBACProvider>
              <AppRoutes />
            </RBACProvider>
          </AuthProvider>
        </Router>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
